import { FIELD } from "./fieldKinds.js";
import { formatDateRange, formatLocation } from "../lib/format.js";
import { COMMON_EVENT_REGISTRATION_FIELDS } from "../features/registration/formConfig.js";

// Medical CME event type. wizardSteps drive the questionnaire; deriveOg builds
// the social-preview metadata stored on the event for SSR.
export default {
  id: "medical-cme",
  category: "professional",
  label: "Medical CME",
  blurb: "Accredited continuing medical education — credits, faculty, agenda.",
  icon: "🩺",
  wizardSteps: [
    {
      id: "basics",
      title: "The essentials",
      fields: [
        { key: "title", label: "Event title", kind: FIELD.TEXT, required: true, placeholder: "NeuroCon 2026" },
        { key: "subtitle", label: "Subtitle / theme", kind: FIELD.TEXT, placeholder: "Advances in Clinical Neurology" },
        { key: "heroImage", label: "Hero image", kind: FIELD.HERO_IMAGE, previewPage: "cover" },
      ],
    },
    {
      id: "when-where",
      title: "When & where",
      fields: [
        { key: "startDate", label: "Start date", kind: FIELD.DATE, required: true },
        { key: "endDate", label: "End date", kind: FIELD.DATE },
        { key: "venue", label: "Venue", kind: FIELD.TEXT, placeholder: "Grand Hyatt" },
        { key: "city", label: "City", kind: FIELD.TEXT },
        { key: "country", label: "Country", kind: FIELD.TEXT },
        { key: "mapsUrl", label: "Google Maps link", kind: FIELD.URL, placeholder: "https://maps.google.com/...", previewPage: "cover" },
      ],
    },
    {
      id: "details",
      title: "Details, contact & registration",
      fields: [
        { key: "description", label: "About the event", kind: FIELD.TEXTAREA, previewPage: "details" },
        { key: "registrationUrl", label: "Registration link", kind: FIELD.URL, previewPage: "cover" },
        {
          key: "collectEventRegistrations",
          label: "Collect event registrations inside einvite",
          kind: FIELD.TOGGLE,
          previewPage: "eventRegistration",
          description: "Adds an in-app registration form for this event. Entries stay visible only to the creator.",
          defaultPatch: { eventRegistrationFields: COMMON_EVENT_REGISTRATION_FIELDS.map((field) => ({ ...field })) },
        },
        {
          key: "eventRegistrationFields",
          label: "Registration form builder",
          kind: FIELD.REGISTRATION_FIELDS,
          previewPage: "eventRegistration",
          showWhen: (details) => !!details.collectEventRegistrations,
        },
        { key: "contacts", label: "Contact persons", kind: FIELD.CONTACTS, previewPage: "closing" },
        {
          key: "collectPreRegistration",
          label: "Subscribe for updates",
          kind: FIELD.TOGGLE,
          previewPage: "subscribe",
          description: "Adds a simple public form where invitees can subscribe with their mobile number or email for event updates.",
        },
      ],
    },
    {
      id: "accreditation",
      title: "Accreditation",
      fields: [
        { key: "cmeCredits", label: "CME credits", kind: FIELD.TEXT, placeholder: "12 AMA PRA Category 1 Credits™" },
        { key: "accreditedBy", label: "Accredited by", kind: FIELD.TEXT, placeholder: "ACCME" },
      ],
    },
    {
      id: "program",
      title: "Program",
      fields: [
        { key: "topics", label: "Topics", kind: FIELD.CHIPS, placeholder: "Add a topic and press Enter", previewPage: "topics" },
        { key: "speakers", label: "Faculty / speakers", kind: FIELD.SPEAKERS, previewPage: "speakers" },
        { key: "schedule", label: "Daywise schedule", kind: FIELD.SCHEDULE, itemLabel: "session", previewPage: "schedule" },
        { key: "workshops", label: "Workshops", kind: FIELD.SCHEDULE, itemLabel: "workshop", previewPage: "workshops" },
      ],
    },
    {
      id: "custom-slides",
      title: "Custom slides",
      fields: [
        { key: "customSlides", label: "Slides", kind: FIELD.CUSTOM_SLIDES, previewPage: "customSlide" },
      ],
    },
  ],
  deriveOg(details = {}) {
    const when = formatDateRange(details.startDate, details.endDate);
    const where = formatLocation(details);
    return {
      title: details.title || "A CME event",
      description: [when, where, details.cmeCredits].filter(Boolean).join(" · "),
    };
  },
};
